/**
 * Générateur — Il mobilise les configurations et la trigonométrie pour
 * calculer des longueurs et des angles.
 * Attendu ID : 242 | Niveau : 3ème | Domaine : Géométrie plane (d17, O5)
 *
 * Source doc prof (séances D3S2-D3S3, 26 exos dont extraits de brevet). Plan
 * auto-validé 2026-07-18 (TODO §6) : « calculatrice » (sin/tan au centième),
 * « equations » (produit en croix, préalable technique), « choixrapport »
 * (cos, sin ou tan ? — la question a. des exos), « longueur » (multiplier OU
 * diviser : les deux sens), « angle » (cos⁻¹/sin⁻¹/tan⁻¹ au degré),
 * « probleme » (lampadaire/cratère : modélisation), « telesiege » (brevet :
 * le dénivelé caché), « deuxtemps » (enchaîner deux triangles, avec la
 * valeur arrondie — les deux chemins d'arrondi coïncident par construction).
 * Arrondis PARTAGÉS gen↔solveur avec garde anti-pivot partout.
 */

import { randomChoice, randInt, tex, makeQcm, makeTexteTrou } from '../../../utils';

const ATTENDU_ID = 242;

/** Angle nommé par trois lettres, en notation du programme : \widehat{RPQ}. */
const ang = (s) => `\\widehat{${s}}`;
/** Décimal français protégé dans une formule KaTeX. */
const d = (s) => String(s).replace(',', '{,}');
const L_CALC = 'Sinus et tangente à la calculatrice';
const L_EQ = 'Résoudre par produit en croix';
const L_CHOIX = 'Cosinus, sinus ou tangente ?';
const L_LONGUEUR = 'Calculer une longueur';
const L_ANGLE = 'Calculer un angle';
const L_PROBLEME = 'Trigonométrie en situation';
const L_TELESIEGE = 'Le télésiège (extrait de brevet)';
const L_DEUXTEMPS = 'En deux temps (deux triangles)';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Affichages FR sans zéro final, depuis des entiers (dixièmes / centièmes).
const fr10 = (dix) => (dix % 10 === 0 ? String(dix / 10) : `${Math.trunc(dix / 10)},${Math.abs(dix % 10)}`);
const fr100 = (cent) => {
    if (cent % 100 === 0) return String(cent / 100);
    if (cent % 10 === 0) return `${Math.trunc(cent / 100)},${(Math.abs(cent) % 100) / 10}`;
    return `${Math.trunc(cent / 100)},${String(Math.abs(cent) % 100).padStart(2, '0')}`;
};
// Garde anti-pivot : la valeur ×10^p n'est pas à cheval sur un .5 d'arrondi.
const stable = (v, p) => { const w = v * (10 ** p); return Math.abs(w - Math.floor(w) - 0.5) > 0.04; };
const rad = (deg) => (deg * Math.PI) / 180;

// Lettres : triangle PQR rectangle en R, angle étudié RPQ (sommet P).
const TRIPLES_RECT = [['M', 'N', 'O'], ['J', 'E', 'U'], ['A', 'B', 'C'], ['K', 'I', 'D'], ['S', 'D', 'E'], ['R', 'S', 'T'], ['F', 'V', 'U'], ['I', 'J', 'K']];

// ═════════ « calculatrice » ═════════

const ANGLES_CALC = [20, 25, 30, 35, 40, 50, 55, 60, 65, 70, 75, 83];

const genCalculatriceTrous = () => {
    let a; let b; let sa; let ta; let tb;
    for (let essai = 0; essai < 80; essai++) {
        a = randomChoice(ANGLES_CALC);
        b = randomChoice(ANGLES_CALC.filter(x => x !== a));
        if (!stable(Math.sin(rad(a)), 2) || !stable(Math.tan(rad(a)), 2) || !stable(Math.tan(rad(b)), 2)) continue;
        sa = Math.round(Math.sin(rad(a)) * 100);
        ta = Math.round(Math.tan(rad(a)) * 100);
        tb = Math.round(Math.tan(rad(b)) * 100);
        break;
    }
    const phrase = `$\\sin(${a}°) \\approx {{${fr100(sa)}}} \\qquad \\tan(${a}°) \\approx {{${fr100(ta)}}}`
        + ` \\qquad \\tan(${b}°) \\approx {{${fr100(tb)}}}$`;
    const payload = makeTexteTrou(phrase,
        // Le mode degrés est une consigne d'usage de la calculatrice, pas un
        // indice mathématique : elle reste, en phrase.
        '<p>À la calculatrice, réglée en MODE DEGRÉS, donne les valeurs arrondies AU CENTIÈME.</p>'
        + '<p><b>Complète.</b></p>',
        { ...opts(L_CALC), mode: 'saisie' });
    payload._meta.params = { a, b, sa, ta, tb };
    return payload;
};

// ═════════ « equations » — produit en croix ═════════

const genEquationsTrous = () => {
    // a. x/p = k (q/r avec q = r·k)  → x = p·k ; b. p2/y = q2/r2 (r2 = q2·k2) → y = p2·k2.
    const r1 = randomChoice([2, 3, 4, 5]);
    const k1 = randInt(2, 4);
    const p1 = randomChoice([15, 25, 35, 12, 18, 22]);      // dixièmes
    const x = p1 * k1;                                      // dixièmes
    const r2 = randomChoice([2, 3, 4, 5].filter(v => v !== r1));
    const k2 = randInt(2, 4);
    const p2 = randomChoice([14, 16, 24, 26, 32]);          // dixièmes
    const y = p2 * k2;                                      // dixièmes
    const phrase = `$\\text{a. } x = {{${fr10(x)}}} \\qquad \\text{b. } y = {{${fr10(y)}}}$`;
    const payload = makeTexteTrou(phrase,
        // « (produit en croix) » donnait la technique de résolution.
        '<p>Résous chaque équation :</p>'
        + `<p>a. ${tex(`\\dfrac{x}{${d(fr10(p1))}} = \\dfrac{${r1 * k1}}{${r1}}`)}`
        + `&nbsp;&nbsp;&nbsp; b. ${tex(`\\dfrac{${d(fr10(p2))}}{y} = \\dfrac{${r2}}{${r2 * k2}}`)}</p>`
        + '<p><b>Donne les valeurs exactes de x et y.</b></p>',
        { ...opts(L_EQ), mode: 'saisie' });
    payload._meta.params = { p1, r1, k1, x, p2, r2, k2, y };
    return payload;
};

// ═════════ « choixrapport » — QCM ═════════

const genChoixRapportQcm = () => {
    const [P, Q, R] = randomChoice(TRIPLES_RECT);
    const hyp = `${P}${Q}`; const adj = `${P}${R}`; const opp = `${Q}${R}`;
    const cas = randomChoice([
        { connu: adj, cherche: hyp, bon: 'le cosinus' },
        { connu: hyp, cherche: adj, bon: 'le cosinus' },
        { connu: opp, cherche: hyp, bon: 'le sinus' },
        { connu: hyp, cherche: opp, bon: 'le sinus' },
        { connu: opp, cherche: adj, bon: 'la tangente' },
        { connu: adj, cherche: opp, bon: 'la tangente' },
    ]);
    const v = fr10(randInt(30, 90));
    const alpha = randInt(20, 70);
    const payload = makeQcm(['le cosinus', 'le sinus', 'la tangente'], cas.bon,
        `<p>Le triangle ${P}${Q}${R} est rectangle en ${R}. On connaît ${tex(`${cas.connu} = ${d(v)}`)} cm `
        + `et ${tex(`${ang(`${R}${P}${Q}`)} = ${alpha}°`)} ; on veut calculer ${cas.cherche}.</p>`
        + `<p><b>Quel rapport trigonométrique de l'angle ${tex(ang(`${R}${P}${Q}`))} relie ces deux `
        + 'longueurs ?</b></p>',
        opts(L_CHOIX));
    payload._meta.params = { P, Q, R, connu: cas.connu, cherche: cas.cherche, bon: cas.bon };
    return payload;
};

// ═════════ « longueur » — multiplier OU diviser ═════════

const genLongueurTrous = () => {
    const [P, Q, R] = randomChoice(TRIPLES_RECT);
    const [D, E, F] = randomChoice(TRIPLES_RECT.filter(t => t[0] !== P));
    let h; let alpha; let va; let a2; let beta; let vb;
    for (let essai = 0; essai < 200; essai++) {
        h = randInt(40, 95);                                // dixièmes ; hypoténuse connue
        alpha = randInt(20, 70);
        const exa = (h / 10) * Math.cos(rad(alpha));        // côté adjacent = h·cos α
        a2 = randInt(30, 80);                               // dixièmes ; côté adjacent connu
        beta = randInt(20, 65);
        const exb = (a2 / 10) / Math.cos(rad(beta));        // hypoténuse = a/cos β
        if (!stable(exa, 1) || !stable(exb, 1)) continue;
        va = Math.round(exa * 10);
        vb = Math.round(exb * 10);
        if (va !== vb) break;
    }
    const phrase = `$\\text{a. } ${P}${R} \\approx {{${fr10(va)}}}\\ \\text{cm} \\qquad`
        + ` \\text{b. } ${D}${E} \\approx {{${fr10(vb)}}}\\ \\text{cm}$`;
    const payload = makeTexteTrou(phrase,
        `<p>a. Le triangle ${P}${Q}${R} est rectangle en ${R} : l'hypoténuse ${tex(`${P}${Q} = ${d(fr10(h))}`)} cm `
        + `et ${tex(`${ang(`${R}${P}${Q}`)} = ${alpha}°`)}. Calcule ${P}${R}.</p>`
        + `<p>b. Le triangle ${D}${E}${F} est rectangle en ${F} : ${tex(`${D}${F} = ${d(fr10(a2))}`)} cm et `
        + `${tex(`${ang(`${F}${D}${E}`)} = ${beta}°`)}. Calcule l'hypoténuse ${D}${E}.</p>`
        // « Un coup on multiplie, un coup on divise ! » annonçait la différence
        // entre les deux questions, qui EST l'objet de la carte.
        + '<p><b>Arrondis au dixième de cm.</b></p>',
        { ...opts(L_LONGUEUR), mode: 'saisie' });
    payload._meta.params = { P, Q, R, h, alpha, va, D, E, F, a2, beta, vb };
    return payload;
};

// ═════════ « angle » — cos⁻¹ / tan⁻¹ ═════════

const genAngleTrous = () => {
    const [P, Q, R] = randomChoice(TRIPLES_RECT);
    const [D, E, F] = randomChoice(TRIPLES_RECT.filter(t => t[0] !== P));
    let a; let h; let angA; let u; let v; let angB;
    for (let essai = 0; essai < 200; essai++) {
        a = randInt(20, 60); h = randInt(a + 10, 95);       // dixièmes, a < h
        const exa = Math.acos((a / 10) / (h / 10)) / Math.PI * 180;
        u = randInt(25, 70); v = randInt(25, 85);           // dixièmes ; deux côtés de l'angle droit
        const exb = Math.atan((v / 10) / (u / 10)) / Math.PI * 180;
        if (!stable(exa, 0) || !stable(exb, 0)) continue;
        angA = Math.round(exa);
        angB = Math.round(exb);
        if (angA !== angB) break;
    }
    const phrase = `$\\text{a. } ${ang(`${R}${P}${Q}`)} \\approx {{${angA}}}° \\qquad`
        + ` \\text{b. } ${ang(`${F}${D}${E}`)} \\approx {{${angB}}}°$`;
    const payload = makeTexteTrou(phrase,
        `<p>a. Le triangle ${P}${Q}${R} est rectangle en ${R} : ${tex(`${P}${R} = ${d(fr10(a))}`)} cm et `
        + `l'hypoténuse ${tex(`${P}${Q} = ${d(fr10(h))}`)} cm. Calcule ${tex(ang(`${R}${P}${Q}`))}.</p>`
        // « (les deux côtés de l'angle droit) » désignait le rapport à choisir
        // (tangente) sans que l'élève ait à le reconnaître.
        + `<p>b. Le triangle ${D}${E}${F} est rectangle en ${F} : ${tex(`${D}${F} = ${d(fr10(u))}`)} cm et `
        + `${tex(`${E}${F} = ${d(fr10(v))}`)} cm. Calcule ${tex(ang(`${F}${D}${E}`))}.</p>`
        + '<p><b>Arrondis au degré.</b> La calculatrice doit être en mode degrés.</p>',
        { ...opts(L_ANGLE), mode: 'saisie' });
    payload._meta.params = { P, Q, R, a, h, angA, D, E, F, u, v, angB };
    return payload;
};

// ═════════ « probleme » — lampadaire / cratère ═════════

const genProblemeTrous = () => {
    if (randInt(0, 1) === 1) {
        // Lampadaire : angle du cône de lumière avec le sol = tan⁻¹(H/r).
        // `angle` et non `ang` : `ang` est le helper « angle chapeauté ».
        let H; let r; let angle;
        for (let essai = 0; essai < 120; essai++) {
            H = randInt(22, 34);                            // dixièmes de m
            r = randInt(7, 16);                             // dixièmes de m
            const ex = Math.atan((H / 10) / (r / 10)) / Math.PI * 180;
            if (!stable(ex, 0)) continue;
            angle = Math.round(ex);
            break;
        }
        const phrase = `$\\text{L'angle mesure environ } {{${angle}}}°$`;
        const payload = makeTexteTrou(phrase,
            `<p>Un lampadaire de ${fr10(H)} m de haut éclaire au sol un disque de ${fr10(r)} m de rayon. `
            + 'Le rayon lumineux qui touche le bord du disque forme, avec le sol, un angle dont la tangente '
            + `vaut ${fr10(H)}/${fr10(r)} (hauteur sur rayon).</p>`
            + '<p><b>Calcule cet angle, arrondi au degré.</b></p>',
            { ...opts(L_PROBLEME), mode: 'saisie' });
        payload._meta.params = { variante: 'lampadaire', H, r, ang: angle };
        return payload;
    }
    // Cratère : profondeur = d·tan α (petit angle, grande distance).
    // `dist` et non `d` : `d` est le helper « décimal protégé » du module.
    let dist; let alpha10; let prof;
    for (let essai = 0; essai < 120; essai++) {
        dist = randInt(15, 40);                             // km
        alpha10 = randInt(32, 88);                          // dixièmes de degré
        const ex = dist * Math.tan(rad(alpha10 / 10));
        if (!stable(ex, 1)) continue;
        prof = Math.round(ex * 10);
        break;
    }
    const habillage = randomChoice(['cratère lunaire', 'canyon', 'fosse océanique']);
    const phrase = `$\\text{La profondeur vaut environ } {{${fr10(prof)}}}\\ \\text{km}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Depuis le bord C d'un ${habillage}, on vise le point le plus bas B : la visée fait un angle `
        + `de ${fr10(alpha10)}° sous l'horizontale. Le point D, à la verticale de B et à l'horizontale de C, `
        + `vérifie ${tex(`CD = ${dist}`)} km ; le triangle BCD est rectangle en D, donc `
        + `${tex(`BD = CD \\times \\tan(${d(fr10(alpha10))}°)`)}.</p>`
        // « Un petit angle, une grande distance ! » commentait le résultat avant
        // que l'élève l'obtienne.
        + '<p><b>Calcule la profondeur BD, arrondie au dixième de km.</b></p>',
        { ...opts(L_PROBLEME), mode: 'saisie' });
    payload._meta.params = { variante: 'cratere', d: dist, alpha10, prof };
    return payload;
};

// ═════════ « telesiege » — le dénivelé caché ═════════

const genTelesiegeTrous = () => {
    // `angle` et non `ang` : `ang` est le helper « angle chapeauté » du module.
    let alt1; let deniv; let cable; let angle;
    for (let essai = 0; essai < 120; essai++) {
        alt1 = randInt(1200, 1900);
        deniv = randInt(300, 620);
        cable = deniv + randInt(700, 1300);
        const ex = Math.asin(deniv / cable) / Math.PI * 180;
        if (!stable(ex, 0)) continue;
        angle = Math.round(ex);
        break;
    }
    const phrase = `$\\text{Dénivelé : } {{${deniv}}}\\ \\text{m} \\qquad \\text{angle : environ } {{${angle}}}°$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un télésiège part de la gare A, à ${alt1} m d'altitude, et arrive à la gare B, à `
        + `${alt1 + deniv} m d'altitude. Le câble mesure ${cable} m. Dans le triangle rectangle formé `
        + 'par le câble, l\'horizontale et la verticale, le DÉNIVELÉ est le côté opposé à l\'angle que '
        + 'fait le câble avec l\'horizontale.</p>'
        // « (la donnée cachée !) » annonçait que le dénivelé n'était pas donné :
        // le repérer EST la première étape de l'exercice.
        + '<p><b>Calcule le dénivelé, puis l\'angle du câble avec l\'horizontale, arrondi au degré.</b></p>',
        { ...opts(L_TELESIEGE), mode: 'saisie' });
    payload._meta.params = { alt1, deniv, cable, ang: angle };
    return payload;
};

// ═════════ « deuxtemps » — enchaîner deux triangles ═════════

const genDeuxTempsTrous = () => {
    let b; let alpha; let beta; let pl10; let rl10;
    for (let essai = 0; essai < 400; essai++) {
        b = randInt(25, 60);                                // dixièmes ; PK
        alpha = randInt(25, 55);
        beta = randInt(25, 55);
        if (beta === alpha) continue;
        const plEx = (b / 10) / Math.cos(rad(alpha));       // PL exact
        if (!stable(plEx, 1)) continue;
        pl10 = Math.round(plEx * 10);
        const rlDeRounded = (pl10 / 10) * Math.cos(rad(beta));
        const rlExact = plEx * Math.cos(rad(beta));
        if (!stable(rlDeRounded, 1) || !stable(rlExact, 1)) continue;
        rl10 = Math.round(rlDeRounded * 10);
        if (rl10 !== Math.round(rlExact * 10)) continue;    // les deux chemins d'arrondi coïncident
        if (rl10 !== pl10) break;
    }
    const phrase = `$\\text{a. } PL \\approx {{${fr10(pl10)}}}\\ \\text{cm} \\qquad`
        + ` \\text{b. } RL \\approx {{${fr10(rl10)}}}\\ \\text{cm}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Les triangles PKL et PRL ont des angles droits en K et en R. ${tex(`PK = ${d(fr10(b))}`)} cm ; `
        + `dans PKL, ${tex(`${ang('KPL')} = ${alpha}°`)} ; dans PRL, ${tex(`${ang('PLR')} = ${beta}°`)}.</p>`
        + '<p>On ne peut pas calculer RL directement : AUCUNE longueur de PRL n\'est connue. '
        + `On calcule d'abord ${tex(`PL = \\dfrac{PK}{\\cos(${alpha}°)}`)}, puis `
        + `${tex(`RL = PL \\times \\cos(${beta}°)`)}.</p>`
        + '<p><b>Calcule PL, puis RL, au dixième de cm.</b> Tu peux utiliser la valeur arrondie de PL.</p>',
        { ...opts(L_DEUXTEMPS), mode: 'saisie' });
    payload._meta.params = { b, alpha, beta, pl10, rl10 };
    return payload;
};

const GENERATORS = {
    calculatrice_texte_trou_saisie: genCalculatriceTrous,
    equations_texte_trou_saisie: genEquationsTrous,
    choixrapport_qcm_unique: genChoixRapportQcm,
    longueur_texte_trou_saisie: genLongueurTrous,
    angle_texte_trou_saisie: genAngleTrous,
    probleme_texte_trou_saisie: genProblemeTrous,
    telesiege_texte_trou_saisie: genTelesiegeTrous,
    deuxtemps_texte_trou_saisie: genDeuxTempsTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'calculatrice', label: L_CALC, formatsList: buildFormats('calculatrice') },
    { id: 'equations', label: L_EQ, formatsList: buildFormats('equations') },
    { id: 'choixrapport', label: L_CHOIX, formatsList: buildFormats('choixrapport') },
    { id: 'longueur', label: L_LONGUEUR, formatsList: buildFormats('longueur') },
    { id: 'angle', label: L_ANGLE, formatsList: buildFormats('angle') },
    { id: 'probleme', label: L_PROBLEME, formatsList: buildFormats('probleme') },
    { id: 'telesiege', label: L_TELESIEGE, formatsList: buildFormats('telesiege') },
    { id: 'deuxtemps', label: L_DEUXTEMPS, formatsList: buildFormats('deuxtemps') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
